import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

const DEFAULT_ENDPOINT =
  "https://api.eu.peliqan.io/2364/cart/inventory/adjust";
const SYNC_TTL_MS = 60 * 1000;
const STORAGE_KEY_PREFIX = "inventory-sync:";

/**
 * @typedef {object} InventorySyncPayload
 * @property {string} productId
 * @property {string | number | null} [articleId]
 * @property {string | null} [articleCode]
 * @property {string[]} variantIds
 * @property {{ variantId: string, articlePriceId: string | number | null }[]} [variants]
 * @property {string} sectionId
 * @property {string} shop
 */

/**
 * @typedef {object} InventorySyncApiResponse
 * @property {string} [status]
 * @property {{ items_updated?: number, result?: { success?: boolean } }} [updates]
 */

class ProductInventorySync extends Component {
  requiredRefs = ["payload"];

  /** @type {(event: Event) => void} */
  #onVariantUpdate = (event) => {
    const payload = this.#getPayload();
    if (!payload) return;

    const eventDetail = /** @type {{ data?: { productId?: string | number } } | undefined} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    const eventProductId = eventDetail?.data?.productId;

    // Ignore variant events from other products.
    if (eventProductId && String(eventProductId) !== String(payload.productId)) {
      return;
    }

    void this.#syncOnLoad();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#cleanupOldSyncKeys();
    const closestSection = this.closest(".shopify-section, dialog");
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    void this.#syncOnLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest(".shopify-section, dialog");
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  async #syncOnLoad() {
    const payload = this.#getPayload();
    if (!payload) return;

    const storageKey = `${STORAGE_KEY_PREFIX}${payload.productId}`;
    if (this.#isRecentlySynced(storageKey)) return;

    const endpoint = this.#buildEndpoint(payload);
    this.dataset.syncStatus = "in-progress";

    try {
      const requestBody = JSON.stringify({
        products: [payload],
      });

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: requestBody,
      });

      if (!response.ok) {
        throw new Error(`Inventory sync failed with status ${response.status}`);
      }

      const responseData = await this.#parseApiResponse(response);
      if (!this.#isSuccessfulResponse(responseData)) {
        throw new Error("Inventory sync API returned unsuccessful result");
      }

      this.#markSynced(storageKey);
      this.dataset.syncStatus = "success";
    } catch (error) {
      this.dataset.syncStatus = "error";
      console.error("Failed to sync product inventory on PDP load:", error);
    }
  }

  /**
   * @returns {InventorySyncPayload | null}
   */
  #getPayload() {
    const payloadElement = this.refs.payload;
    if (!(payloadElement instanceof HTMLScriptElement)) return null;

    const rawPayload = payloadElement.textContent?.trim();
    if (!rawPayload) return null;

    try {
      const payload = JSON.parse(rawPayload);
      if (!payload?.productId) return null;
      return payload;
    } catch (error) {
      console.error("Invalid product inventory sync payload:", error);
      return null;
    }
  }

  /**
   * @param {string} storageKey
   * @returns {boolean}
   */
  #isRecentlySynced(storageKey) {
    try {
      const lastSyncedAt = window.sessionStorage.getItem(storageKey);
      if (!lastSyncedAt) return false;

      return Date.now() - Number(lastSyncedAt) < SYNC_TTL_MS;
    } catch {
      return false;
    }
  }

  /**
   * Removes stale or invalid inventory sync keys from sessionStorage.
   */
  #cleanupOldSyncKeys() {
    try {
      const keysToRemove = [];

      for (let i = 0; i < window.sessionStorage.length; i += 1) {
        const key = window.sessionStorage.key(i);
        if (!key || !key.startsWith(STORAGE_KEY_PREFIX)) continue;

        const rawTimestamp = window.sessionStorage.getItem(key);
        const timestamp = Number(rawTimestamp);

        if (!rawTimestamp || Number.isNaN(timestamp)) {
          keysToRemove.push(key);
          continue;
        }

        if (Date.now() - timestamp >= SYNC_TTL_MS) {
          keysToRemove.push(key);
        }
      }

      for (const key of keysToRemove) {
        window.sessionStorage.removeItem(key);
      }
    } catch {
      // Ignore sessionStorage errors.
    }
  }

  /**
   * @param {string} storageKey
   */
  #markSynced(storageKey) {
    try {
      window.sessionStorage.setItem(storageKey, String(Date.now()));
    } catch {
      // Ignore sessionStorage errors in private mode.
    }
  }

  /**
   * Builds endpoint URL by replacing {productid} placeholder.
   * Uses articleId first because external inventory system maps by article id.
   * @param {InventorySyncPayload} payload
   * @returns {string}
   */
  #buildEndpoint(payload) {
    const endpointTemplate = this.dataset.endpoint || DEFAULT_ENDPOINT;
    const productIdentifier = payload.articleId ?? payload.productId;

    if (String(endpointTemplate).includes("{productid}")) {
      return endpointTemplate.replace(
        "{productid}",
        encodeURIComponent(String(productIdentifier)),
      );
    }

    return endpointTemplate;
  }

  /**
   * @param {Response} response
   * @returns {Promise<InventorySyncApiResponse | null>}
   */
  async #parseApiResponse(response) {
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.toLowerCase().includes("application/json")) return null;

    try {
      const parsed = await response.json();
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * @param {InventorySyncApiResponse | null} responseData
   * @returns {boolean}
   */
  #isSuccessfulResponse(responseData) {
    // If backend does not return JSON, rely on HTTP status check.
    if (!responseData) return true;

    return responseData.status === "ok";
  }
}

if (!customElements.get("product-inventory-sync")) {
  customElements.define("product-inventory-sync", ProductInventorySync);
}
